import React, { useState, useEffect } from "react";

import { Grid, IconButton } from "@mui/material";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { FiEdit, FiSave, FiTrash2 } from "react-icons/fi";

import { useAppState } from "../../../../context/StateContext";
import { Instruction } from "../../../../models/instruction";
import Input from "../../../Form/Input";
import { DefaultTooltip } from "../../Tooltip";
import useStyles from "./styles";

interface Props {
  isOnViewMode?: boolean;
  instruction: Instruction;
  onRemoveInstruction: (instruction: Instruction) => void;
  onUpdateInstruction: (instruction: Instruction) => void;
  isOrder?: boolean;
}

const InstructionItem = ({
  isOnViewMode = false,
  instruction,
  onUpdateInstruction,
  onRemoveInstruction,
  isOrder = false,
}: Props) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const [remark, setRemark] = useState<string>("");
  const [updatingRemark, setUpdatingRemark] = useState<boolean>(true);

  const handleRemarkDescription = (): string => {
    const getNameUser = () => {
      if (instruction.createdBy)
        return `by ${instruction.createdBy.name} ${instruction.createdBy.lastName}`;

      return "Unknown";
    };

    if (instruction.id && instruction.createdAt) {
      return `${getNameUser()} at ${format(new Date(instruction.createdAt), "dd/MM/yy h:mmaaa")}`;
    }

    return `${getNameUser()} at ${format(new Date(), "dd/MM/yy h:mmaaa")}`;
  };

  useEffect(() => {
    if (instruction) {
      setRemark(instruction.remarks || "");
      setUpdatingRemark(!isOnViewMode && !(instruction.remarks && instruction.remarks.length > 0));
    }
  }, [instruction]);

  const remarksIsEmpty = instruction?.remarks ? instruction?.remarks?.trim()?.length === 0 : true;

  return (
    <Grid
      className={classes.instructionItemGrid}
      container
      direction="column"
      justifyContent="flex-start"
      alignItems="flex-start"
      spacing={2}
    >
      <span className={classes.whenUpperTitle}>{translate("instruction").toUpperCase()}</span>
      <span className={classes.titleItem} data-cy="title-instruction">
        {instruction.name}
      </span>
      <div className={classes.instructionItemDiv}>
        {updatingRemark ? (
          <Input
            formControlClassName={classes.remarkField}
            id="remark-item"
            required
            placeholder={translate("inputYourRemarkAndPressEnter")}
            data-cy="input-remark"
            value={remark}
            onChange={(event) => {
              setRemark(event.target.value);
              instruction.remarks = event.target.value;
              instruction.createdBy = currentUser;
              instruction.createdById = currentUser?.id;
            }}
            onKeyPress={(e) => {
              if (e.key === "Enter") onUpdateInstruction(instruction);
            }}
          />
        ) : (
          <div className={classes.remarkDiv}>
            <span className={classes.remark} data-cy="title-remark">
              {instruction.remarks}
            </span>

            <span className={classes.remarkDescription}>{handleRemarkDescription()}</span>
          </div>
        )}

        {updatingRemark && (
          <DefaultTooltip title={remarksIsEmpty ? translate("theFieldIsEmpty") : ""}>
            <div>
              <IconButton disabled={remarksIsEmpty} onClick={() => setUpdatingRemark(false)}>
                <FiSave size={18} />
              </IconButton>
            </div>
          </DefaultTooltip>
        )}
        {!isOnViewMode && (
          <>
            {!isOrder && (
              <DefaultTooltip title={translate("delete")}>
                <div>
                  <IconButton
                    className={classes.deleteButton}
                    onClick={() => onRemoveInstruction(instruction)}
                  >
                    <FiTrash2 size={18} />
                  </IconButton>
                </div>
              </DefaultTooltip>
            )}
            <DefaultTooltip title={translate("edit")}>
              <div>
                <IconButton disabled={updatingRemark} onClick={() => setUpdatingRemark(true)}>
                  <FiEdit size={18} />
                </IconButton>
              </div>
            </DefaultTooltip>
          </>
        )}
      </div>
      <div className={classes.secondaryLine}></div>
    </Grid>
  );
};

export default InstructionItem;
